import dayjs from 'dayjs';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { CouncilRegion } from 'shared-components';
import { ConfirmPrompt } from '../../Abstracts/Components';
import useConfirmPrompt from '../../Hooks/UseConfirmPrompt';
import { FoodLicenseWithDocuments, RegionRequired } from '../../Services/InsuranceLicenseService';
import { ErrorHandler } from '../../Services/UserApi';
import { formatRegionList, getRequiredLicenseRegions } from './InsuranceLicenseUtils';
import InsuranceLicenseAddCta from './InsuranceLicenseAddCta';
import FoodLicenseTable from './FoodLicenseTable';

type Props = {
	licenses: FoodLicenseWithDocuments[];
	councilRegions: CouncilRegion[];
	regionsRequired: RegionRequired[];
	removeFoodLicense: (licenseId: number) => Promise<unknown>;
};

export default function FoodLicenseSummarySection(props: Props) {
	const { licenses, councilRegions, regionsRequired, removeFoodLicense } = props;
	const [removingLicenseId, setRemovingLicenseId] = useState<number | null>(null);
	const [licenseIdToDelete, setLicenseIdToDelete] = useState<number | null>(null);
	const nowUnix = dayjs().unix();
	const requiredLicenseRegions = getRequiredLicenseRegions({
		foodLicenses: licenses,
		publicLiability: [],
		councilRegions,
		regionsRequired,
		nextBookings: [],
	});
	const warning =
		requiredLicenseRegions.length === 0
			? ''
			: licenses.length > 0
				? `You have no valid food license for upcoming bookings at ${formatRegionList(requiredLicenseRegions)}. Please add a valid license using the button above.`
				: `You have no valid food license for upcoming bookings in the ${formatRegionList(requiredLicenseRegions)}. Please add a valid license using the button above.`;

	const deleteLicensePrompt = useConfirmPrompt({
		title: 'Remove Food License',
		text: 'Are you sure you want to remove this food license? This action cannot be undone.',
		icon: 'fa fa-exclamation-triangle',
		confirmButton: {
			text: 'Remove',
			color: 'danger',
			icon: 'fa fa-trash',
		},
		onConfirm: async () => {
			if (licenseIdToDelete === null) {
				return;
			}
			setRemovingLicenseId(licenseIdToDelete);
			try {
				await removeFoodLicense(licenseIdToDelete);
				toast.success('Food license removed');
			} catch (error) {
				ErrorHandler.toast(error);
				throw error;
			} finally {
				setRemovingLicenseId(null);
				setLicenseIdToDelete(null);
			}
		},
	});

	const onRemoveLicense = (licenseId: number) => {
		setLicenseIdToDelete(licenseId);
		deleteLicensePrompt.open();
	};

	return (
		<section className="insurance-license-section">
			<h3>Food licenses</h3>
			<div className="card-note">
				If your stall serves food, you will need to provide us the details of your food license for each council
				region you trade in.
			</div>

			<div className="insurance-license-card">
				<InsuranceLicenseAddCta to="/add/food-license" text="Add food license" />

				{warning && (
					<div className="food-license-warning-region">
						<span className="card-warning">{warning}</span>
					</div>
				)}

				{licenses.length > 0 && (
					<FoodLicenseTable
						licenses={licenses}
						councilRegions={councilRegions}
						nowUnix={nowUnix}
						removingLicenseId={removingLicenseId}
						onRemoveLicense={onRemoveLicense}
					/>
				)}
			</div>
			<ConfirmPrompt
				open={deleteLicensePrompt.isOpen}
				onClose={deleteLicensePrompt.close}
				{...deleteLicensePrompt.promptProps}
			/>
		</section>
	);
}
